import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setOrganizations, setActiveOrganization, setCurrentUser } from "../redux/actions";
import { UserService, AuthService, CommonService, ToastService } from "../services";
import { LoadingSpinner } from "../components";

/**
 * Held until the session is known.
 *
 * Deliberately carries NO app chrome. The bug this exists for (SD-4350) was
 * that an unauthenticated visitor saw the full console -- sidebar, quick-access
 * cards, the org name -- for ~300ms before being redirected to the login
 * screen. Rendering the layout here would reproduce exactly that.
 */
const SessionGate = () => (
  <div
    role="status"
    aria-live="polite"
    className="w-full h-screen flex-center"
    data-cy="session-gate"
  >
    <span className="sr-only">Loading</span>
    <span aria-hidden="true">
      <LoadingSpinner />
    </span>
  </div>
);

export const Preloader = ({
  children,
}) => {
  const dispatch = useDispatch();

  /**
   * Whether the session has been resolved either way.
   *
   * `useEffect` runs AFTER paint, so without this the authenticated shell is
   * painted before the first request is even sent -- the flash was
   * deterministic, not a race.
   *
   * Released as soon as the ORGANIZATIONS call settles, either way. That call
   * is the authentication probe: if it comes back at all -- 200 or 500 -- the
   * browser is not being redirected, so there is nothing left to hide. A 401
   * never settles (see NEVER_SETTLES in http.service.ts), so the gate stays
   * shut while the browser navigates to the login screen, which is the whole
   * point of it.
   *
   * Deliberately NOT waiting for the profile call as well. Chaining both meant
   * a failed orgs call still waited on the profile one, so a 500 plus a slow
   * profile left a permanent spinner -- the app has to become usable even when
   * the bootstrap fails. The profile finishes in the background, as it already
   * did when children rendered immediately.
   */
  const [ready, setReady] = useState(false);

  const loadProfile = (orgs, cb) => {
    UserService.getProfile(false).then(user => {
      dispatch(setCurrentUser(user));
      dispatch(setActiveOrganization(user.default_org || orgs[0]));
      cb()
    }).catch(err => {
      ToastService.showHttpError(err, 'Loading profile failed');
      cb()
    });
  }

  const loadAllOrgs = cb => {
    AuthService.getMyOrganizations(false).then(data => {
      dispatch(setOrganizations(data));
      setReady(true);
      cb(data)
    }).catch(err => {
      dispatch(setOrganizations([]));
      ToastService.showHttpError(err, 'Loading organizations failed');
      setReady(true);
      cb([])
    });
  }

  useEffect(() => {
    loadAllOrgs(orgs => { loadProfile(orgs, () => { }); });
  }, []);

  if (!ready) {
    return <SessionGate />;
  }

  return <>{children}</>;
};
